import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Page,
  Card,
  Box,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Divider,
  Spinner,
  Banner,
  Button,
  ButtonGroup,
  Collapsible,
  Icon,
} from "@shopify/polaris";
import { ChevronDownIcon, ChevronUpIcon } from "@shopify/polaris-icons";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { AppDispatch } from "configureStore";
import {
  fetchDepartmentRow,
  Department,
  DepartmentMember,
} from "services/departments.service";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  MemberAvatar,
  departmentColor,
  departmentColorSoft,
  getInitials,
  isMemberInactive,
} from "./department-visuals";
import DepartmentDetailSkeleton from "./department-detail-skeleton";
import HeadDataAccessBadge from "./head-data-access-badge";
import { useAppSelector } from "hooks";
import useIsPhone from "features/files/use-is-phone";
import { canManageDepartments, canOpenPersonnel } from "features/personnel/personnel-helpers";

// Code-split: @xyflow/react only loads when the Graph tab is opened.
const DepartmentGraph = lazy(() => import("./department-graph"));

/** One labeled figure in the stats strip. */
function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone?: string;
}) {
  return (
    <BlockStack gap="050">
      <Text as="span" tone="subdued" variant="bodySm">
        {label}
      </Text>
      <Text as="span" variant="headingLg" tone={tone as any}>
        {value}
      </Text>
    </BlockStack>
  );
}

function MemberRow({
  member,
  isHead,
  subgroupBadge,
  onOpenCard,
}: {
  member: DepartmentMember;
  isHead?: boolean;
  subgroupBadge?: string | null;
  /** Opens the member's darbuotojo kortelė; omitted when the viewer cannot open it. */
  onOpenCard?: () => void;
}) {
  const { t } = useTranslation();
  const inactive = isMemberInactive(member);
  return (
    <Box padding="400">
      <InlineStack align="space-between" blockAlign="center" gap="300">
        <InlineStack gap="300" blockAlign="center">
          <MemberAvatar member={member} size={32} interactive />
          <BlockStack gap="050">
            <InlineStack gap="150" blockAlign="center">
              {onOpenCard ? (
                <button
                  type="button"
                  className="text-left font-medium text-gray-900 hover:text-blue-700 hover:underline"
                  onClick={onOpenCard}
                  title={t("personnel_card_title")}
                >
                  {member.name}
                </button>
              ) : (
                <Text as="span" fontWeight="medium">
                  {member.name}
                </Text>
              )}
              {isHead ? <Badge tone="info">{t("head")}</Badge> : null}
              {subgroupBadge ? <Badge>{subgroupBadge}</Badge> : null}
              {inactive ? <Badge>{t("inactive")}</Badge> : null}
            </InlineStack>
            {member.position ? (
              <Text as="span" tone="subdued" variant="bodySm">
                {member.position}
              </Text>
            ) : null}
          </BlockStack>
        </InlineStack>
        {member.email ? (
          <Text as="span" tone="subdued" variant="bodySm">
            {member.email}
          </Text>
        ) : null}
      </InlineStack>
    </Box>
  );
}

function CollapsibleMemberSection({
  title,
  count,
  open,
  onToggle,
  actions,
  children,
}: {
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Box padding="400">
        <InlineStack align="space-between" blockAlign="center" gap="200">
          <button
            type="button"
            onClick={onToggle}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              textAlign: "left",
            }}
            aria-expanded={open}
          >
            <Icon source={(open ? ChevronUpIcon : ChevronDownIcon) as any} />
            <Text as="span" variant="headingSm" fontWeight="semibold">
              {title}
            </Text>
            <Badge>{String(count)}</Badge>
          </button>
          {actions || null}
        </InlineStack>
      </Box>
      <Collapsible open={open} id={`section-${title}`}>
        {count > 0 ? (
          children
        ) : (
          <Box padding="400" paddingBlockStart="0">
            <Text as="span" tone="subdued">
              —
            </Text>
          </Box>
        )}
      </Collapsible>
    </div>
  );
}

/**
 * Department detail page (/app/:companyId/departments/:id). Full-width page
 * showing an identity-colored hero, member stats, and the members
 * list / interactive graph. Change history is in the header "History" popover;
 * editing is done via the shared modal.
 */
function DepartmentDetailPage() {
  const { companyId, id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const role = useAppSelector((state) => state.authReducer.role);
  const canOpenCards = canOpenPersonnel(role);
  // The WS specialist reads departments only: no edit / add subgroup buttons.
  const canManage = canManageDepartments(role);
  // The backend still decides per person (403 on the card) — this only hides
  // the link from viewers who have no Personalas at all.
  const cardOpener = (member: DepartmentMember) =>
    canOpenCards && member.userId
      ? () => navigate(`/app/${companyId}/personnel/${member.userId}`)
      : undefined;

  const [department, setDepartment] = useState<Department | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  // The graph needs room: on a phone it is scaled to unreadable text, so the
  // member list opens first and the graph stays one tap away.
  const isPhone = useIsPhone();
  const [view, setView] = useState<"list" | "graph">(isPhone ? "list" : "graph");
  /** Collapsible list sections: parent + each subgroup id. Default all open. */
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    parent: true,
  });

  const load = useCallback(async () => {
    if (!companyId || !id) return;
    setIsLoading(true);
    setIsError(false);
    try {
      const dept = await fetchDepartmentRow(companyId, id);
      setDepartment(dept);
    } catch (e) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, id]);

  useEffect(() => {
    load();
  }, [load]);

  // Ensure new subgroups get an open section entry when data loads.
  useEffect(() => {
    if (!department) return;
    setOpenSections((prev) => {
      const next = { ...prev };
      if (next.parent === undefined) next.parent = true;
      for (const sg of department.subgroups || []) {
        if (next[sg.id] === undefined) next[sg.id] = true;
      }
      return next;
    });
  }, [department]);

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleEdit = () => {
    if (!id) return;
    dispatch(setModalxComponent("DepartmentForm"));
    dispatch(
      setModalxComponentProps({
        id,
        onSaved: load,
        subgroups: department?.subgroups || [],
      }),
    );
  };

  const openSubgroupForm = (subgroupId?: string) => {
    if (!id) return;
    dispatch(
      setModalxOptions({
        title: subgroupId ? t("edit_subgroup") : t("add_subgroup"),
      }),
    );
    dispatch(setModalxComponent("DepartmentForm"));
    dispatch(
      setModalxComponentProps(
        subgroupId
          ? { id: subgroupId, parentDepartment: id, onSaved: load }
          : { parentDepartment: id, onSaved: load },
      ),
    );
  };

  if (!companyId || !id) {
    return null;
  }

  const members = department?.members || [];
  const subgroups = department?.subgroups || [];
  const headId = department?.head?.companyUserId;
  const accent = departmentColor(id);
  const totalCount = members.length;
  const inactiveCount = members.filter(isMemberInactive).length;
  const activeCount = totalCount - inactiveCount;

  return (
    <Page
      fullWidth
      backAction={{
        content: t("departments"),
        onAction: () => navigate(`/app/${companyId}/departments`),
      }}
      title={department?.name || t("department")}
      titleMetadata={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isLoading && <Spinner size="small" />}
          <AuditLogPopover
            companyId={companyId}
            subjectType="Department"
            subjectId={id}
            label={t("history", { defaultValue: "History" })}
          />
        </div>
      }
      primaryAction={canManage ? { content: t("edit"), onAction: handleEdit } : undefined}
    >
      {isError ? (
        <Banner tone="critical">{t("fetch_error")}</Banner>
      ) : isLoading && !department ? (
        <DepartmentDetailSkeleton
          accent={accent}
          accentSoft={departmentColorSoft(id)}
        />
      ) : department ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          <BlockStack gap="400">
            {/* Identity hero + stats */}
            <Card padding="0">
              <div
                style={{
                  background: departmentColorSoft(id),
                  borderLeft: `4px solid ${accent}`,
                }}
              >
                <Box padding="400">
                  <InlineStack gap="300" blockAlign="center" wrap>
                    <div
                      className="dept-avatar"
                      style={{
                        width: 48,
                        height: 48,
                        background: accent,
                        fontSize: 18,
                      }}
                    >
                      {getInitials(department.name)}
                    </div>
                    <BlockStack gap="100">
                      {department.description ? (
                        <Text as="p" tone="subdued">
                          {department.description}
                        </Text>
                      ) : (
                        <Text as="p" tone="subdued">
                          {t("no_description", {
                            defaultValue: "No description",
                          })}
                        </Text>
                      )}
                      <InlineStack gap="150" blockAlign="center">
                        <Text as="span" tone="subdued" variant="bodySm">
                          {t("head")}:
                        </Text>
                        {department.head ? (
                          <InlineStack gap="150" blockAlign="center">
                            <MemberAvatar member={department.head} size={22} />
                            <Text as="span" fontWeight="medium">
                              {department.head.name}
                              {department.head.position
                                ? ` — ${department.head.position}`
                                : ""}
                            </Text>
                            {department.headHasDataAccess ? <HeadDataAccessBadge /> : null}
                          </InlineStack>
                        ) : (
                          <Text as="span">—</Text>
                        )}
                      </InlineStack>
                    </BlockStack>
                  </InlineStack>
                </Box>
              </div>
              <Divider />
              <Box padding="400">
                <InlineStack gap="1200" wrap>
                  <Stat label={t("members")} value={totalCount} />
                  <Stat
                    label={t("active", { defaultValue: "Active" })}
                    value={activeCount}
                    tone="success"
                  />
                  <Stat
                    label={t("inactive")}
                    value={inactiveCount}
                    tone={inactiveCount > 0 ? "critical" : undefined}
                  />
                  <Stat label={t("subgroups")} value={subgroups.length} />
                </InlineStack>
              </Box>
            </Card>

            {/* Subgroups */}
            <Card padding="0">
              <Box padding="400">
                <InlineStack
                  align="space-between"
                  blockAlign="center"
                  gap="200"
                >
                  <Text as="h2" variant="headingMd">
                    {t("subgroups")} ({subgroups.length})
                  </Text>
                  {canManage && (
                    <Button size="slim" onClick={() => openSubgroupForm()}>
                      {t("add_subgroup")}
                    </Button>
                  )}
                </InlineStack>
              </Box>
              <Divider />
              {subgroups.length > 0 ? (
                subgroups.map((sg, i) => (
                  <div key={sg.id}>
                    {i > 0 ? <Divider /> : null}
                    <Box padding="400">
                      <InlineStack
                        align="space-between"
                        blockAlign="center"
                        gap="300"
                      >
                        <BlockStack gap="100">
                          <InlineStack gap="150" blockAlign="center">
                            <Text as="span" fontWeight="semibold">
                              {sg.name}
                            </Text>
                            <Badge>{`${sg.memberCount} ${t("members").toLowerCase()}`}</Badge>
                          </InlineStack>
                          <InlineStack gap="150" blockAlign="center">
                            <Text as="span" tone="subdued" variant="bodySm">
                              {t("subgroup_head")}: {sg.head?.name || "—"}
                            </Text>
                            {sg.head && sg.headHasDataAccess ? <HeadDataAccessBadge /> : null}
                          </InlineStack>
                        </BlockStack>
                        {canManage && (
                          <Button
                            size="slim"
                            onClick={() => openSubgroupForm(sg.id)}
                          >
                            {t("edit")}
                          </Button>
                        )}
                      </InlineStack>
                    </Box>
                  </div>
                ))
              ) : (
                <Box padding="400">
                  <Text as="span" tone="subdued">
                    {t("no_subgroups")}
                  </Text>
                </Box>
              )}
            </Card>

            {/* Members */}
            <Card padding="0">
              <Box padding="400">
                <InlineStack
                  align="space-between"
                  blockAlign="center"
                  gap="200"
                >
                  <Text as="h2" variant="headingMd">
                    {t("members")} ({members.length})
                  </Text>
                  <ButtonGroup variant="segmented">
                    <Button
                      size="slim"
                      pressed={view === "graph"}
                      onClick={() => setView("graph")}
                    >
                      {t("view_graph")}
                    </Button>
                    <Button
                      size="slim"
                      pressed={view === "list"}
                      onClick={() => setView("list")}
                    >
                      {t("view_list")}
                    </Button>
                  </ButtonGroup>
                </InlineStack>
              </Box>
              <Divider />
              {view === "graph" ? (
                <Box padding="400">
                  <Suspense
                    fallback={
                      <InlineStack align="center">
                        <Spinner size="small" />
                      </InlineStack>
                    }
                  >
                    <DepartmentGraph department={department} accent={accent} getCardOpener={cardOpener} />
                  </Suspense>
                </Box>
              ) : (
                <>
                  <CollapsibleMemberSection
                    title={t("department_members")}
                    count={members.length}
                    open={openSections.parent !== false}
                    onToggle={() => toggleSection("parent")}
                  >
                    {members.map((m, i) => (
                      <motion.div
                        key={`parent:${m.companyUserId}`}
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.2,
                          delay: Math.min(i, 12) * 0.02,
                        }}
                      >
                        {i > 0 ? <Divider /> : null}
                        <MemberRow
                          member={m}
                          isHead={headId === m.companyUserId}
                          onOpenCard={cardOpener(m)}
                        />
                      </motion.div>
                    ))}
                  </CollapsibleMemberSection>

                  {subgroups.map((sg) => {
                    const sgMembers = sg.members || [];
                    const sgHeadId = sg.head?.companyUserId;
                    return (
                      <div key={sg.id}>
                        <Divider />
                        <CollapsibleMemberSection
                          title={sg.name}
                          count={sgMembers.length}
                          open={openSections[sg.id] !== false}
                          onToggle={() => toggleSection(sg.id)}
                          actions={
                            canManage ? (
                              <Button
                                size="slim"
                                onClick={() => openSubgroupForm(sg.id)}
                              >
                                {t("edit")}
                              </Button>
                            ) : undefined
                          }
                        >
                          {sgMembers.map((m, i) => (
                            <motion.div
                              key={`sg:${sg.id}:${m.companyUserId}`}
                              initial={{ opacity: 0, x: -6 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{
                                duration: 0.2,
                                delay: Math.min(i, 12) * 0.02,
                              }}
                            >
                              {i > 0 ? <Divider /> : null}
                              <MemberRow
                                member={m}
                                isHead={sgHeadId === m.companyUserId}
                                subgroupBadge={t("subgroup")}
                                onOpenCard={cardOpener(m)}
                              />
                            </motion.div>
                          ))}
                        </CollapsibleMemberSection>
                      </div>
                    );
                  })}
                </>
              )}
            </Card>
          </BlockStack>
        </motion.div>
      ) : (
        <Text as="p">{t("no_departments")}</Text>
      )}
    </Page>
  );
}

export default DepartmentDetailPage;
